import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  ForecastHour,
  ForecastResponse,
  HeightFrame,
  UnitProfile,
  WindPoint
} from "../types";
import {
  formatHeight,
  formatSpeed,
  formatStabilityRate,
  formatTemperature,
  heightUnitLabel,
  speedUnitLabel,
  stabilityRateUnitLabel,
  temperatureUnitLabel
} from "../units/conversions";
import {
  heightAtTemperature,
  niceCeiling,
  stabilityLayers,
  windColor
} from "../windgram/physics";
import { renderStabilityField } from "../windgram/stabilityField";

interface WindgramChartProps {
  forecast: ForecastResponse;
  frame: HeightFrame;
  units: UnitProfile;
  selectedHour: number;
  onSelectHour: (hour: number) => void;
}

const DESKTOP_WIDTH = 1280;
const DESKTOP_HEIGHT = 780;
const COMPACT_WIDTH = 480;
const COMPACT_HEIGHT = 560;
const COMPACT_HOUR_COUNT = 5;
const LEFT = 76;
const RIGHT = 30;
const TOP = 48;
const BOTTOM = 158;

const TEXTURE_MARKS = {
  smooth: { state: "smooth", symbol: "S", legend: "Smooth" },
  textured: { state: "textured", symbol: "T", legend: "Textured" },
  rough: { state: "rough", symbol: "R", legend: "Rough" },
  inactive: { state: "inactive", symbol: "—", legend: "Inactive" },
  unknown: { state: "unknown", symbol: "?", legend: "Unavailable" }
} as const;

const TEXTURE_LEGEND_STATES = [
  "smooth",
  "textured",
  "rough",
  "inactive",
  "unknown"
] as const;

export function textureMark(state: string) {
  return TEXTURE_MARKS[state as keyof typeof TEXTURE_MARKS] ?? TEXTURE_MARKS.unknown;
}

function formatIsothermLabel(targetC: number, units: UnitProfile, locale?: string): string {
  const fahrenheit = targetC * (9 / 5) + 32;
  return `${formatTemperature(fahrenheit, units, 0, locale)}${temperatureUnitLabel(units)}`;
}

function useCompactWindgram(): boolean {
  const [compact, setCompact] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 820px)").matches
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 820px)");
    const update = () => setCompact(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return compact;
}

export function compactHourWindow<T extends { hour: number }>(
  hours: T[],
  selectedHour: number,
  count = COMPACT_HOUR_COUNT
): T[] {
  if (hours.length <= count) return hours;
  const selectedIndex = Math.max(
    0,
    hours.findIndex((hour) => hour.hour === selectedHour)
  );
  const start = Math.min(
    Math.max(selectedIndex - Math.floor(count / 2), 0),
    hours.length - count
  );
  return hours.slice(start, start + count);
}

function frameLabel(frame: HeightFrame, units: UnitProfile, translate: (key: string, options?: Record<string, unknown>) => string): string {
  const unit = units === "metric" ? "m" : "ft";
  if (frame === "altitude_model_agl_ft") return translate("svg.modelAgl", { unit });
  if (frame === "altitude_launch_agl_ft") return translate("svg.launchAgl", { unit });
  return `${unit} MSL`;
}

function diagnosticHeight(
  hour: ForecastHour,
  kind: "thermal" | "usable" | "cloudbase",
  frame: HeightFrame,
  forecast: ForecastResponse
): number | null {
  if (kind === "thermal") {
    if (frame === "altitude_msl_ft") return hour.thermal_top_msl_ft;
    if (frame === "altitude_model_agl_ft") return hour.thermal_top_model_agl_ft;
    return hour.thermal_top_launch_agl_ft;
  }
  if (kind === "usable") {
    if (frame === "altitude_msl_ft") return hour.usable_top_msl_ft;
    if (frame === "altitude_model_agl_ft") return hour.usable_top_model_agl_ft;
    return hour.usable_top_launch_agl_ft;
  }
  if (hour.cloudbase_msl_ft === null) return null;
  if (frame === "altitude_msl_ft") return hour.cloudbase_msl_ft;
  if (frame === "altitude_launch_agl_ft") return hour.cloudbase_launch_agl_ft;
  return hour.cloudbase_msl_ft - forecast.source.model_terrain_ft;
}

function mslToFrame(
  heightMsl: number | null,
  frame: HeightFrame,
  forecast: ForecastResponse
): number | null {
  if (heightMsl === null) return null;
  if (frame === "altitude_msl_ft") return heightMsl;
  if (frame === "altitude_model_agl_ft") {
    return heightMsl - forecast.source.model_terrain_ft;
  }
  return forecast.launch_ft === null ? null : heightMsl - forecast.launch_ft;
}

function windHeight(point: WindPoint, frame: HeightFrame): number | null {
  const value = point[frame];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function polylinePath(
  points: Array<{ x: number; y: number } | null>
): string[] {
  const paths: string[] = [];
  let current: string[] = [];
  for (const point of points) {
    if (point === null) {
      if (current.length > 1) paths.push(current.join(" "));
      current = [];
      continue;
    }
    current.push(`${point.x.toFixed(1)},${point.y.toFixed(1)}`);
  }
  if (current.length > 1) paths.push(current.join(" "));
  return paths;
}

function WindBarb({
  x,
  y,
  speed,
  direction,
  inFlightLayer,
  units,
  locale,
  translate
}: {
  x: number;
  y: number;
  speed: number;
  direction: number | null;
  inFlightLayer: boolean;
  units: UnitProfile;
  locale: string;
  translate: (key: string, options?: Record<string, unknown>) => string;
}) {
  const color = windColor(speed, inFlightLayer);
  const rounded = Math.round(speed / 5) * 5;
  const full = Math.floor(rounded / 10);
  const half = rounded % 10 >= 5;
  if (direction === null || speed < 2.5) {
    return (
      <circle cx={x} cy={y} r={3.8} fill="none" stroke={color} strokeWidth={1.5}>
        <title>{translate("svg.calmWind", { speed: formatSpeed(speed, units, 1, locale), unit: speedUnitLabel(units) })}</title>
      </circle>
    );
  }
  return (
    <g
      transform={`translate(${x} ${y}) rotate(${direction})`}
      stroke={color}
      strokeLinecap="round"
      strokeWidth={speed > 15 && inFlightLayer ? 2.3 : 1.6}
      className="wind-barb"
    >
      <title>{translate("svg.windFrom", {
        speed: formatSpeed(speed, units, 1, locale),
        unit: speedUnitLabel(units),
        direction: new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(direction)
      })}</title>
      <line x1={0} y1={9} x2={0} y2={-12} />
      {Array.from({ length: Math.min(full, 4) }).map((_, index) => (
        <line
          key={index}
          x1={0}
          y1={-11 + index * 4}
          x2={7}
          y2={-7 + index * 4}
        />
      ))}
      {half && <line x1={0} y1={-11 + full * 4} x2={4} y2={-9 + full * 4} />}
    </g>
  );
}

function Canopy({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} className="canopy-mark">
      <path d="M -10 1 Q -5 -7 0 -5 Q 5 -7 10 1 L 5 -1 L 0 -4 L -5 -1 Z" />
      <path d="M -5 -1 L 0 6 L 5 -1 M 0 6 L 0 9" fill="none" />
    </g>
  );
}

function Cloud({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} className="cloud-mark">
      <circle cx={-6} cy={1} r={5} />
      <circle cx={0} cy={-3} r={7} />
      <circle cx={7} cy={1} r={5} />
      <rect x={-8} y={1} width={17} height={5} rx={2.5} />
    </g>
  );
}

function TextureBandValue({
  x,
  y,
  state,
  label,
  compact
}: {
  x: number;
  y: number;
  state: string;
  label: string;
  compact: boolean;
}) {
  if (!compact) {
    return (
      <text className="character-value" x={x} y={y}>
        {label}
      </text>
    );
  }

  const mark = textureMark(state);
  return (
    <g className={`texture-mark texture-state-${mark.state}`}>
      <title>{label}</title>
      <rect x={x - 14} y={y - 17} width={28} height={24} rx={12} />
      <text x={x} y={y}>{mark.symbol}</text>
    </g>
  );
}

export function WindgramChart({
  forecast,
  frame,
  units,
  selectedHour,
  onSelectHour
}: WindgramChartProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const allHours = forecast.hours;
  const compact = useCompactWindgram();
  const hours = useMemo(
    () => (compact ? compactHourWindow(allHours, selectedHour) : allHours),
    [allHours, compact, selectedHour]
  );
  const width = compact ? COMPACT_WIDTH : DESKTOP_WIDTH;
  const height = compact ? COMPACT_HEIGHT : DESKTOP_HEIGHT;
  const plotBottom = height - BOTTOM;
  const plotHeight = plotBottom - TOP;
  const columnWidth = (width - LEFT - RIGHT) / Math.max(hours.length, 1);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const hourPickerRef = useRef<HTMLDivElement>(null);
  const selectedForecastHour = allHours.find((hour) => hour.hour === selectedHour) ?? allHours[0];
  const selectedProfileAvailability = selectedForecastHour?.availability?.profile;
  const profileReasonKey = `models.reason.${selectedProfileAvailability?.reason ?? "unknown"}`;
  const translatedProfileReason = t(profileReasonKey);

  const domain = useMemo(() => {
    const diagnosticHeights = allHours.flatMap((hour) =>
      (["thermal", "usable", "cloudbase"] as const)
        .map((kind) => diagnosticHeight(hour, kind, frame, forecast))
        .filter((value): value is number => value !== null)
    );
    const modelGround =
      frame === "altitude_msl_ft"
        ? forecast.source.model_terrain_ft
        : frame === "altitude_model_agl_ft"
          ? 0
          : forecast.launch_ft === null
            ? 0
            : forecast.source.model_terrain_ft - forecast.launch_ft;
    const launchGround =
      frame === "altitude_msl_ft"
        ? forecast.launch_ft
        : frame === "altitude_model_agl_ft"
          ? forecast.launch_ft === null
            ? null
            : forecast.launch_ft - forecast.source.model_terrain_ft
          : 0;
    const floor = Math.floor(
      Math.min(modelGround, launchGround ?? modelGround, ...diagnosticHeights) / 1000
    ) * 1000;
    const minimumCeiling = frame === "altitude_msl_ft" ? 14_000 : 9_000;
    const ceiling = Math.min(
      frame === "altitude_msl_ft" ? 20_000 : 15_000,
      Math.max(
        minimumCeiling,
        niceCeiling(Math.max(...diagnosticHeights, floor + 8000) + 2000)
      )
    );
    return { floor, ceiling, modelGround, launchGround };
  }, [allHours, forecast, frame]);

  const x = (index: number) => LEFT + columnWidth * (index + 0.5);
  // Sampled continuously between columns and levels. Falls back to the flat
  // layer rectangles when no canvas exists, so the chart still renders.
  const stabilityField = useMemo(
    () =>
      renderStabilityField({
        profiles: hours.map((hour) => hour.profile),
        frame,
        minHeight: domain.floor,
        maxHeight: domain.ceiling
      }),
    [hours, frame, domain.floor, domain.ceiling]
  );

  const y = (height: number) =>
    TOP +
    ((domain.ceiling - height) / (domain.ceiling - domain.floor)) * plotHeight;
  const ticks: number[] = [];
  for (
    let height = Math.ceil(domain.floor / 1000) * 1000;
    height <= domain.ceiling;
    height += 1000
  ) {
    ticks.push(height);
  }

  const thermalPoints = hours.map((hour, index) => {
    const height = diagnosticHeight(hour, "thermal", frame, forecast);
    return height === null ? null : { x: x(index), y: y(height) };
  });
  const coolerPoints = hours.map((hour, index) => {
    const height = mslToFrame(
      hour.thermal_top_sensitivity.cooler_surface_msl_ft,
      frame,
      forecast
    );
    return height === null ? null : { x: x(index), y: y(height) };
  });
  const warmerPoints = hours.map((hour, index) => {
    const height = mslToFrame(
      hour.thermal_top_sensitivity.warmer_surface_msl_ft,
      frame,
      forecast
    );
    return height === null ? null : { x: x(index), y: y(height) };
  });
  const selectedIndex = Math.max(
    0,
    hours.findIndex((hour) => hour.hour === selectedHour)
  );

  useEffect(() => {
    if (!compact) return;
    const picker = hourPickerRef.current;
    const selected = picker?.querySelector<HTMLElement>(
      `[data-hour="${selectedHour}"]`
    );
    if (!picker || !selected) return;
    const target = selected.offsetLeft + selected.offsetWidth / 2 - picker.clientWidth / 2;
    picker.scrollTo({
      left: Math.max(0, Math.min(target, picker.scrollWidth - picker.clientWidth)),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth"
    });
  }, [compact, selectedHour]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (compact || !scroller || scroller.scrollWidth <= scroller.clientWidth) {
      return;
    }
    const centerInSvg = LEFT + columnWidth * (selectedIndex + 0.5);
    const target =
      scroller.scrollWidth * (centerInSvg / width) - scroller.clientWidth / 2;
    scroller.scrollTo({
      left: Math.max(0, Math.min(target, scroller.scrollWidth - scroller.clientWidth)),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth"
    });
  }, [columnWidth, compact, selectedIndex, width]);

  return (
    <div
      className={`windgram-chart${compact ? " compact" : ""}`}
      role="region"
      aria-label={t("nav.windgram")}
    >
      {compact && (
        <div
          className="windgram-hour-picker"
          ref={hourPickerRef}
          role="group"
          aria-label={t("controls.forecastDay")}
        >
          {allHours.map((hour) => (
            <button
              type="button"
              key={hour.hour}
              data-hour={hour.hour}
              className={hour.hour === selectedHour ? "active" : ""}
              aria-pressed={hour.hour === selectedHour}
              onClick={() => onSelectHour(hour.hour)}
            >
              {String(hour.hour).padStart(2, "0")}
            </button>
          ))}
        </div>
      )}
      {selectedForecastHour && selectedForecastHour.profile.length === 0 ? (
        <div className="availability-note" role="status">
          <strong>{t("charts.noProfile")}</strong>{" "}
          {t("charts.surfaceOnly")}{" "}
          {t("charts.unavailableReason", {
            reason: translatedProfileReason === profileReasonKey
              ? t("models.reason.unknown")
              : translatedProfileReason
          })}
        </div>
      ) : null}
      <div
        className={`windgram-canvas${compact ? " compact" : ""}`}
        ref={scrollerRef}
        role={compact ? undefined : "group"}
        aria-label={compact ? undefined : t("charts.scrollA11y")}
        tabIndex={compact ? undefined : 0}
      >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-labelledby="windgram-title windgram-description"
      >
        <title id="windgram-title">{t("page.forecastTitle", { site: forecast.site })}</title>
        <desc id="windgram-description">
          {t("charts.description")}
        </desc>
        <defs>
          <clipPath id="plot-clip">
            <rect x={LEFT} y={TOP} width={width - LEFT - RIGHT} height={plotHeight} />
          </clipPath>
          <pattern id="ground-pattern" width="8" height="8" patternUnits="userSpaceOnUse">
            <rect width="8" height="8" fill="#352f28" />
            <path d="M0 8 L8 0" stroke="#4b4237" strokeWidth="2" />
          </pattern>
        </defs>

        <rect className="plot-paper" x={LEFT} y={TOP} width={width - LEFT - RIGHT} height={plotHeight} />

        <g clipPath="url(#plot-clip)">
          {stabilityField ? (
            <image
              href={stabilityField}
              xlinkHref={stabilityField}
              x={LEFT}
              y={TOP}
              width={width - LEFT - RIGHT}
              height={plotHeight}
              preserveAspectRatio="none"
            />
          ) : null}

          {hours.flatMap((hour, index) =>
            stabilityLayers(hour.profile, frame).map((layer, layerIndex) => (
              <rect
                key={`${hour.hour}-${layerIndex}`}
                x={LEFT + index * columnWidth}
                y={y(layer.upperFt)}
                width={columnWidth + 0.5}
                height={Math.max(y(layer.lowerFt) - y(layer.upperFt), 0)}
                fill={stabilityField ? "transparent" : layer.color}
              >
                <title>{`${hour.hour}:00 · ${layer.label} · ${formatStabilityRate(
                  layer.rateCPer1000Ft,
                  units,
                  2,
                  locale
                )} ${stabilityRateUnitLabel(units)}`}</title>
              </rect>
            ))
          )}

          {ticks.map((height) => (
            <line
              key={height}
              className="altitude-grid"
              x1={LEFT}
              x2={width - RIGHT}
              y1={y(height)}
              y2={y(height)}
            />
          ))}

          {[-20, -10, 0, 10, 20, 30].flatMap((targetC) => {
            const points = hours.map((hour, index) => {
              const height = heightAtTemperature(hour.profile, targetC, frame);
              return height === null ? null : { x: x(index), y: y(height) };
            });
            return polylinePath(points).map((path, pathIndex) => (
              <polyline
                key={`${targetC}-${pathIndex}`}
                points={path}
                className={targetC === 0 ? "freezing-contour" : "isotherm-contour"}
              >
                <title>{t("charts.isothermTitle", {
                  temperature: formatIsothermLabel(targetC, units, locale)
                })}</title>
              </polyline>
            ));
          })}

          {polylinePath(coolerPoints).map((path, index) => (
            <polyline key={`cool-${index}`} points={path} className="sensitivity-line" />
          ))}
          {polylinePath(warmerPoints).map((path, index) => (
            <polyline key={`warm-${index}`} points={path} className="sensitivity-line" />
          ))}
          {polylinePath(thermalPoints).map((path, index) => (
            <polyline key={`top-${index}`} points={path} className="thermal-top-line" />
          ))}

          {hours.flatMap((hour, index) => {
            const thermal = diagnosticHeight(hour, "thermal", frame, forecast);
            const used: number[] = [];
            return hour.wind_profile
              .map((wind) => ({ wind, height: windHeight(wind, frame) }))
              .filter(
                (item): item is { wind: WindPoint; height: number } =>
                  item.height !== null &&
                  item.height >= domain.floor &&
                  item.height <= domain.ceiling
              )
              .filter((item) => {
                if (used.some((height) => Math.abs(height - item.height) < 520)) {
                  return false;
                }
                used.push(item.height);
                return true;
              })
              .map(({ wind, height }) => (
                <WindBarb
                  key={`${hour.hour}-${wind.kind}-${height}`}
                  x={x(index)}
                  y={y(height)}
                  speed={wind.speed_kt}
                  direction={wind.direction_degrees}
                  inFlightLayer={thermal !== null && height <= thermal}
                  units={units}
                  locale={locale}
                  translate={t}
                />
              ));
          })}

          {hours.map((hour, index) => {
            const usable = diagnosticHeight(hour, "usable", frame, forecast);
            return usable === null ? null : <Canopy key={hour.hour} x={x(index)} y={y(usable)} />;
          })}
          {hours.map((hour, index) => {
            if (!hour.cumulus_potential) return null;
            const cloudbase = diagnosticHeight(hour, "cloudbase", frame, forecast);
            return cloudbase === null ? null : <Cloud key={hour.hour} x={x(index)} y={y(cloudbase)} />;
          })}

          {domain.launchGround !== null && (
            <line
              className="launch-line"
              x1={LEFT}
              x2={width - RIGHT}
              y1={y(domain.launchGround)}
              y2={y(domain.launchGround)}
            />
          )}
          <rect
            x={LEFT}
            y={y(domain.modelGround)}
            width={width - LEFT - RIGHT}
            height={plotBottom - y(domain.modelGround)}
            fill="url(#ground-pattern)"
          />
          <line
            x1={LEFT}
            x2={width - RIGHT}
            y1={y(domain.modelGround)}
            y2={y(domain.modelGround)}
            className="model-ground-line"
          />
          <rect
            className="selected-hour-column"
            x={LEFT + selectedIndex * columnWidth}
            y={TOP}
            width={columnWidth}
            height={plotHeight}
          />
        </g>

        {ticks.map((height) => (
          <g key={`label-${height}`} className="axis-label">
            <text x={LEFT - 10} y={y(height) + 4} textAnchor="end">
              {formatHeight(height, units, 0, locale)}
            </text>
            {!compact && (
              <text x={width - RIGHT + 9} y={y(height) + 4}>
                {formatHeight(height, units, 0, locale)}
              </text>
            )}
          </g>
        ))}
        <text className="axis-title" x={LEFT - 10} y={TOP - 15} textAnchor="end">
          {frameLabel(frame, units, t)}
        </text>

        <g className="hour-band">
          <rect x={LEFT} y={plotBottom} width={width - LEFT - RIGHT} height={BOTTOM - 32} />
          {hours.map((hour, index) => {
            const stress = hour.thermal_character.strong_core;
            return (
              <g key={`band-${hour.hour}`}>
                <line
                  x1={LEFT + index * columnWidth}
                  x2={LEFT + index * columnWidth}
                  y1={plotBottom}
                  y2={height - 32}
                />
                <text className="hour-label" x={x(index)} y={plotBottom + 25}>
                  {String(hour.hour).padStart(2, "0")}
                </text>
                <text className="wstar-value" x={x(index)} y={plotBottom + 48}>
                  {hour.w_star_ms === null ? "—" : new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false }).format(hour.w_star_ms)}
                </text>
                <text className="stress-value" x={x(index)} y={plotBottom + 68}>
                  {stress.available && stress.air_climb_low_ms !== null
                    ? `${new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false }).format(stress.air_climb_low_ms)}–${stress.air_climb_high_ms === null ? "—" : new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false }).format(stress.air_climb_high_ms)}`
                    : "—"}
                </text>
                <TextureBandValue
                  x={x(index)}
                  y={plotBottom + 90}
                  state={hour.thermal_character.texture.state}
                  label={t(`inspector.state.${hour.thermal_character.texture.state}`)}
                  compact={compact}
                />
                <rect
                  x={LEFT + index * columnWidth}
                  y={TOP}
                  width={columnWidth}
                  height={height - TOP - 32}
                  className="hour-hit-target"
                  role={compact ? undefined : "button"}
                  tabIndex={compact ? undefined : 0}
                  aria-label={compact ? undefined : t("charts.inspectHour", { hour: `${String(hour.hour).padStart(2, "0")}:00` })}
                  onClick={() => onSelectHour(hour.hour)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectHour(hour.hour);
                    }
                  }}
                />
              </g>
            );
          })}
          <line x1={width - RIGHT} x2={width - RIGHT} y1={plotBottom} y2={height - 32} />
        </g>

        <g className="band-labels">
          <text x={LEFT - 10} y={plotBottom + 25}>{t("inspector.hour", { hour: "" }).trim()}</text>
          <text x={LEFT - 10} y={plotBottom + 48}>W* m/s</text>
          <text x={LEFT - 10} y={plotBottom + 68}>{t("svg.airmassNote")}</text>
          <text x={LEFT - 10} y={plotBottom + 90}>{t("inspector.axis.texture")}</text>
        </g>
      </svg>
      </div>
      {compact && (
        <div className="texture-key" aria-label={t("inspector.axis.texture")}>
          <strong>{t("inspector.axis.texture")}</strong>
          {TEXTURE_LEGEND_STATES.map((state) => {
            const mark = TEXTURE_MARKS[state];
            return (
              <span key={state}>
                <i
                  className={`texture-key-mark texture-state-${mark.state}`}
                  aria-hidden="true"
                >
                  {mark.symbol}
                </i>
                {t(`inspector.state.${state}`)}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
